import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  GlobalStyles,
  Typography,
} from '@mui/material';
import { AutoFixHigh } from '@mui/icons-material';
import {
  autoAssignAttendees,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { useUserContext } from '../../lib/users-context';
import { Attendee } from '../../lib/types';
import { sortGroups } from '../../lib/groups';
import GroupRosterCard, { groupCardTitle } from './group-roster-card';
import MoveAttendeeDialog from './move-attendee-dialog';
import AttendeeForm from '../attendee-form';
import { filterRosterByQuery } from './roster-filter';

export const PRINT_TARGET_ID = 'roster-print-target';

// Print-only styles. Strategy:
//   1. Hide screen chrome (AppBar, Footer, Snackbar, Dialog, page header,
//      sub-tabs, Manage panel) via display:none so the roster flows naturally
//      starting at the top of the page. We deliberately avoid the
//      visibility:hidden + position:absolute trick because absolutely
//      positioned elements don't honor `break-after: page` reliably,
//      which produces blank pages between groups.
//   2. Force black text and white backgrounds inside the roster regardless
//      of the dark theme.
//   3. Page-break after each group card.
//   4. Reclaim space the screen layout doesn't need on paper: zero out the
//      Card / CardContent padding (the @page margin is the only frame
//      the table needs) and switch the table from `fixed` to `auto`
//      table-layout so the hidden Move column releases its reserved 5%
//      to the visible columns. Without that switch the Status column
//      stays narrow enough to ellipsize the "Here" chip into "H...".
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '@page': { size: 'letter portrait', margin: '0.15in' },
        // Hide every known piece of screen-only chrome.
        '.MuiAppBar-root': { display: 'none !important' },
        'footer, [role="contentinfo"]': { display: 'none !important' },
        '.MuiSnackbar-root, .MuiBackdrop-root, .MuiDialog-root': {
          display: 'none !important',
        },
        '.roster-print-page-header, .roster-print-subtabs': {
          display: 'none !important',
        },
        // Strip dark-theme background from the page itself.
        body: { backgroundColor: 'white !important' },
        // Roster colors: force black text, white card backgrounds.
        [`#${PRINT_TARGET_ID}, #${PRINT_TARGET_ID} *`]: {
          color: 'black !important',
        },
        [`#${PRINT_TARGET_ID} .MuiPaper-root, #${PRINT_TARGET_ID} .MuiCard-root`]:
          {
            backgroundColor: 'white !important',
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
            border: 'none !important',
            margin: '0 !important',
          },
        // Drop the Card's screen padding; @page margin is the only frame
        // the table needs on paper.
        [`#${PRINT_TARGET_ID} .MuiCardContent-root`]: {
          padding: '0 !important',
        },
        [`#${PRINT_TARGET_ID} .MuiTableCell-root`]: {
          borderColor: 'black !important',
          padding: '4px 6px !important',
          // Anchor cell content to the top of the row so a short main
          // row doesn't leave a visual gap above the FA/MC detail row
          // that follows it. With `vertical-align: middle` (MUI's
          // default), short content like "F" / "KS" centers in the row
          // and the bottom half reads as empty space on paper.
          verticalAlign: 'top !important',
        },
        // Let print rows shrink to fit their content. The 56px screen
        // height is breathing room for hover targets / IconButtons; on
        // paper it just wastes vertical space and amplifies the gap
        // before the FA/MC detail row.
        [`#${PRINT_TARGET_ID} table tbody tr`]: {
          height: 'auto !important',
        },
        // Auto-layout in print so `display:none` on the Move column
        // (data-print-hide) actually frees up its 5% column slot.
        [`#${PRINT_TARGET_ID} table`]: {
          tableLayout: 'auto !important',
          width: '100% !important',
        },
        // The screen-side colgroup pins percentage widths via inline
        // styles on each <col>. In auto-layout the browser still uses
        // those as preferred widths, and because the Chip's
        // overflow/text-ellipsis makes its min-content width ~0, the
        // column never grows past its 10% hint to fit "Here". Reset
        // every col to auto so the printed table sizes columns to
        // their actual content.
        [`#${PRINT_TARGET_ID} colgroup col`]: {
          width: 'auto !important',
        },
        // Outlined, transparent chips so colored backgrounds don't print.
        // Force the chip to be at least as wide as its label so a
        // narrow Status cell can't ellipsize "Here" into "H...".
        [`#${PRINT_TARGET_ID} .MuiChip-root`]: {
          backgroundColor: 'transparent !important',
          border: '1px solid black !important',
          maxWidth: 'none !important',
          minWidth: 'max-content !important',
        },
        [`#${PRINT_TARGET_ID} .MuiChip-label`]: {
          overflow: 'visible !important',
          textOverflow: 'clip !important',
        },
        // Page-break after every group except the last (avoids trailing blank).
        [`#${PRINT_TARGET_ID} > .roster-page`]: {
          breakAfter: 'page',
          pageBreakAfter: 'always',
          marginBottom: 0,
        },
        [`#${PRINT_TARGET_ID} > .roster-page:last-child`]: {
          breakAfter: 'auto',
          pageBreakAfter: 'auto',
        },
      },
    }}
  />
);

const sortAttendees = (list: Attendee[]): Attendee[] =>
  [...list].sort((a, b) => {
    const lastDiff = a.last_name.localeCompare(b.last_name, undefined, {
      sensitivity: 'base',
    });
    if (lastDiff !== 0) return lastDiff;
    return a.first_name.localeCompare(b.first_name, undefined, {
      sensitivity: 'base',
    });
  });

const GroupsRoster = ({
  showCheckInStatus = false,
  showGradeCompleted = false,
  showEmergencyContact = true,
  searchQuery = '',
  groupFilterRef = '',
}: {
  showCheckInStatus?: boolean;
  showGradeCompleted?: boolean;
  // Emergency contact column defaults to ON — it's the column volunteers
  // expect to see by default. Admins can hide it (e.g. for a roster-only
  // printout) via the "Show EC" toggle on `groups-page.tsx`.
  showEmergencyContact?: boolean;
  // Case-insensitive name search. Empty / whitespace = no filter.
  // Already debounced by the parent (`groups-page.tsx`).
  searchQuery?: string;
  // Restrict the rendered roster to a single group's card. Empty
  // string = no filter. The Unassigned card is hidden when this is
  // set, since the admin asked for a specific group.
  groupFilterRef?: string;
}) => {
  const { groups, attendees, rotations, activeCheckInDate, activeEvent } =
    useFirebaseContext();
  const { isAdmin, canMoveAttendees } = useUserContext();
  const actor = useActor();
  const toast = useToast();
  const [assigning, setAssigning] = useState(false);
  const [movingAttendee, setMovingAttendee] = useState<Attendee | null>(null);
  // Mirrors the Check-in page's "click name to edit" behavior so admins
  // can fix typos / flags / custom-field values without leaving the
  // Roster tab.
  const [editingAttendee, setEditingAttendee] = useState<Attendee | null>(
    null,
  );

  const rotationNameByRef = useMemo(() => {
    const map = new Map<string, string>();
    rotations.forEach((r) => map.set(r.ref, r.name));
    return map;
  }, [rotations]);

  const { perGroup, unassigned } = useMemo(() => {
    const groupRefs = new Set(groups.map((g) => g.ref));
    const byGroup = new Map<string, Attendee[]>();
    const orphans: Attendee[] = [];
    attendees.forEach((a) => {
      if (a.groupRef && groupRefs.has(a.groupRef)) {
        const list = byGroup.get(a.groupRef);
        if (list) list.push(a);
        else byGroup.set(a.groupRef, [a]);
      } else {
        orphans.push(a);
      }
    });
    return {
      perGroup: sortGroups(groups).map((g) => ({
        group: g,
        attendees: sortAttendees(byGroup.get(g.ref) || []),
      })),
      unassigned: sortAttendees(orphans),
    };
  }, [groups, attendees]);

  // Layered on top of the raw breakdown so auto-assign + admin
  // operations keep using `unassigned` (the full pool), but render
  // only the cards that match the active filters. The group filter
  // narrows the pool first (and hides the Unassigned card when a
  // group is selected); the search filter then drops cards with
  // zero hits and computes the empty-state count.
  const groupNarrowed = useMemo(() => {
    if (!groupFilterRef) return { perGroup, unassigned };
    return {
      perGroup: perGroup.filter(({ group }) => group.ref === groupFilterRef),
      unassigned: [],
    };
  }, [perGroup, unassigned, groupFilterRef]);
  const filtered = useMemo(
    () =>
      filterRosterByQuery(
        groupNarrowed.perGroup,
        groupNarrowed.unassigned,
        searchQuery,
      ),
    [groupNarrowed, searchQuery],
  );
  const isSearching = searchQuery.trim().length > 0;
  const isFiltering = isSearching || Boolean(groupFilterRef);
  const noMatches = isFiltering && filtered.totalMatches === 0;

  const handleAutoAssign = async () => {
    if (!activeEvent || assigning) return;
    setAssigning(true);
    try {
      const result = await autoAssignAttendees(
        actor,
        unassigned,
        attendees,
        groups,
        activeEvent,
      );
      if (result.assigned === 0) {
        const parts: string[] = [];
        if (result.skippedNoGrade > 0)
          parts.push(`${result.skippedNoGrade} missing grade`);
        if (result.skippedNoGroup > 0)
          parts.push(`${result.skippedNoGroup} missing matching group`);
        toast.info(
          parts.length > 0
            ? `Nothing to assign (${parts.join(', ')})`
            : 'No unassigned attendees',
        );
      } else {
        const parts: string[] = [`${result.assigned} attendees assigned`];
        if (result.fulfilledCount > 0)
          parts.push(`${result.fulfilledCount} preferences fulfilled`);
        // Only mention the gender skew when it's actually noticeable
        // (≥ 2 in some group). Perfect/near-perfect balance stays quiet
        // so the toast doesn't get noisy on the typical happy path.
        if (result.maxGenderSkew >= 2)
          parts.push(`max gender skew: ±${result.maxGenderSkew}`);
        if (result.skippedNoGrade > 0)
          parts.push(`${result.skippedNoGrade} skipped (no grade)`);
        if (result.skippedNoGroup > 0)
          parts.push(`${result.skippedNoGroup} skipped (no matching group)`);
        toast.success(parts.join('. '));
      }
    } catch (error) {
      console.error('Auto-assign failed', error);
      toast.error('Auto-assign failed');
    } finally {
      setAssigning(false);
    }
  };

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color='text.secondary'>
          No groups yet. Switch to <strong>Groups</strong> to add one.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {printStyles}
      {noMatches ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color='text.secondary'>
            {isSearching
              ? `No attendees match \u201C${searchQuery.trim()}\u201D`
              : 'No attendees in this group'}
          </Typography>
        </Box>
      ) : (
        <Box id={PRINT_TARGET_ID}>
          {filtered.unassigned.length > 0 && (
            <Box className='roster-page' data-group-ref='__unassigned__'>
              <GroupRosterCard
                title={`Unassigned (${filtered.unassigned.length})`}
                attendees={filtered.unassigned}
                showCheckInStatus={showCheckInStatus}
                showGradeCompleted={showGradeCompleted}
                showEmergencyContact={showEmergencyContact}
                checkInDate={activeCheckInDate}
                onMoveAttendee={canMoveAttendees ? setMovingAttendee : undefined}
                onSelectAttendee={setEditingAttendee}
                headerAction={
                  // Auto-assign always operates on the full unassigned
                  // pool. We disable it while a search or group filter
                  // is active so the admin doesn't act on what looks
                  // like a 1-kid card and accidentally rebalance a
                  // hidden 20-kid pool.
                  isAdmin ? (
                    <Button
                      variant='contained'
                      size='small'
                      startIcon={<AutoFixHigh />}
                      onClick={handleAutoAssign}
                      disabled={assigning || isFiltering}
                      title={
                        isFiltering
                          ? 'Clear filters to auto-assign'
                          : undefined
                      }
                    >
                      {assigning ? 'Assigning…' : 'Auto-assign'}
                    </Button>
                  ) : undefined
                }
              />
            </Box>
          )}
          {filtered.perGroup.map(({ group, attendees: groupAttendees }) => {
            const rotationName = group.rotationRef
              ? rotationNameByRef.get(group.rotationRef)
              : undefined;
            const subtitle = rotationName
              ? `Teachers: ${group.teachers} · ${rotationName}`
              : `Teachers: ${group.teachers}`;
            return (
              <Box
                key={group.ref}
                className='roster-page'
                data-group-ref={group.ref}
              >
                <GroupRosterCard
                  title={groupCardTitle(group)}
                  subtitle={subtitle}
                  attendees={groupAttendees}
                  showCheckInStatus={showCheckInStatus}
                  showGradeCompleted={showGradeCompleted}
                  showEmergencyContact={showEmergencyContact}
                  checkInDate={activeCheckInDate}
                  onMoveAttendee={canMoveAttendees ? setMovingAttendee : undefined}
                  onSelectAttendee={setEditingAttendee}
                />
              </Box>
            );
          })}
        </Box>
      )}
      <MoveAttendeeDialog
        attendee={movingAttendee}
        groups={groups}
        onClose={() => setMovingAttendee(null)}
      />
      <Dialog
        open={Boolean(editingAttendee)}
        onClose={() => setEditingAttendee(null)}
        fullWidth
      >
        {editingAttendee && (
          <>
            <DialogTitle>
              Edit {editingAttendee.first_name} {editingAttendee.last_name}
            </DialogTitle>
            <DialogContent>
              <AttendeeForm
                attendee={editingAttendee}
                onClose={() => setEditingAttendee(null)}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default GroupsRoster;
